<!-- @format -->
<template>
    <div class="company-notes-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>Note</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <div v-if="step === 'addNote' || step === 'updateNote'" class="activity-step">
                <div class="activity-form-row">
                    <div class="input-wrapper contact">
                        <label>Company</label>
                        <company-search
                            v-model="activity.company"
                            :initialId="activity.companyId"
                            :clientId="clientId"
                            :disabled="isCompanySearchDisabled"
                        />
                        <div v-if="!$v.activity.company.required" class="error">
                            A company must be selected
                        </div>
                    </div>
                    <div class="input-wrapper description">
                        <label>Description</label>
                        <froala v-model="activity.description" :config="config" />
                        <div v-if="hasDescriptionError" class="error">
                            A description must be added
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-else-if="step === 'saving' || step === 'loading'"
                class="activity-step saving-step"
            >
                <quick-add-saving-svg />
            </div>
        </div>

        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="action-container">
                <app-button
                    :disabled="!$v.activity.company.required"
                    :loading="steps[step].loading"
                    @click="steps[step].action"
                >
                    {{ steps[step].buttonText }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const CompanySearch = () => import('@/components/company/company-search.vue');
const QuickAddSavingSvg = () => import('@/components/svg/quick-add/quick-add-saving-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';

export default {
    name: 'company-notes-widget',

    components: {
        'app-button': AppButton,
        'company-search': CompanySearch,
        'quick-add-saving-svg': QuickAddSavingSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },

        options: {
            type: Object,
            default: () => {},
        },
    },

    data() {
        return {
            hasDescriptionError: false,
            isCompanySearchDisabled: false,
            clientId: this.options.clientId,

            activity: {
                id: this.options.activityId,
                companyId: this.options.companyId,
                company: null,
                duration: 0,
                description: this.options.description,
            },

            step: 'loading',

            steps: {
                loading: {
                    title: 'Loading',
                    buttonText: 'Loading',
                    action: () => {},
                    loading: true,
                },

                addNote: {
                    buttonText: 'Save',
                    action: this.postNote,
                    loading: false,
                },

                updateNote: {
                    buttonText: 'Save',
                    action: this.patchNote,
                    loading: false,
                },

                saving: {
                    title: 'Saving Note',
                    buttonText: 'Saving',
                    action: () => {},
                    loading: true,
                },
            },
        };
    },

    validations: {
        activity: {
            company: {
                required,
            },

            type: {
                required,
            },
        },
    },

    computed: {
        userId() {
            return this.$store.state.user.id;
        },

        config() {
            return {
                fileUpload: true,
                key: this.$helpers.getFroalaKey(),
                imageUploadParam: 'file',
                fileUploadParam: 'file',

                imageUploadURL: 'https://api.honeycrm.com/api/Files/Froala',
                fileUploadURL: 'https://api.honeycrm.com/api/Files/Froala',

                imageUploadMethod: 'POST',
                fileUploadMethod: 'POST',

                imageMaxSize: 4 * 1024 * 1024,
                fileMaxSize: 4 * 1024 * 1024,

                imageAllowedTypes: ['jpeg', 'jpg', 'png'],
                fileAllowedTypes: ['*'],
                requestHeaders: {
                    Authorization: 'Bearer ' + localStorage.jwt,
                },

                placeholderText: 'Type Away...',
                toolbarButtons: ['bold', 'italic', 'insertLink', 'align', 'insertFile'],
            };
        },
    },

    watch: {},

    created() {
        this.setFroalaDefaults();

        if (this.activity.companyId) {
            this.getInitialCompany().then(() => {
                if (this.activity.id) {
                    this.isCompanySearchDisabled = true;
                    this.getActivity();
                }
            });
        }
    },

    methods: {
        setFroalaDefaults() {
            $.FroalaEditor.DefineIcon('insertFile', {
                template: 'image',
                SRC: 'https://cdn.honeycrm.com/files/attach-icon-white.svg',
                ALT: 'Upload File',
            });
        },

        getInitialCompany() {
            return new Promise((resolve, reject) => {
                const select = '$select=Id,Name,LogoUrl';
                const filter = `$filter=Id eq ${this.activity.companyId}`;
                const restUrl = `/service/Companies?${filter}&${select}`;

                this.$api
                    .get(restUrl)
                    .then((response) => {
                        this.activity.company = {
                            id: response.data.value[0].Id,
                            name: response.data.value[0].Name,
                            imageUrl: response.data.value[0].LogoUrl,
                        };
                        resolve()
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get company', { error });
                        reject();
                    })
                    .finally(() => {
                        this.step = 'addNote';
                    });
            });
        },

        getSelectParam() {
            return '$select=Id,ActivityDate,Description';
        },

        getExpandParam() {
            return '$expand=User($select=Id,FirstName,LastName,ProfileImageUrl)';
        },

        getFilterParam() {
            let filterParam = '';

            const filters = this.getFilters();

            if (filters.length > 0) {
                filterParam = `$filter=${filters.join(' and ')}`;
            }

            return filterParam;
        },

        getFilters() {
            const queryFilters = [];

            if (this.activity.id) {
                queryFilters.push(`Id eq ${this.activity.id}`);
            }

            return queryFilters;
        },

        getQueryParams() {
            return [
                this.getSelectParam(),
                this.getExpandParam(),
                this.getFilterParam(),
                '$count=true',
            ].join('&');
        },

        getActivity() {
            const restUrl = `/service/CompanyNotes?ClientId=${this.clientId}&${this.getQueryParams()}`;

            this.$api
                .get(restUrl)
                .then((response) => {
                    const activityResponse = response.data.value[0];
                    this.activity.id = this.options.activityId;
                    this.activity.description = activityResponse.Description;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get note information.', { error });
                })
                .finally(() => {
                    this.step = 'updateNote';
                });
        },

        postNote() {
            const restUrl = '/api/CompanyNotes';

            if (this.activity.company) {
                if (this.activity.description !== '') {
                    this.step = 'saving';
                    this.hasDescriptionError = false;

                    const noteData = {
                        ActivityDate: this.$moment.utc().format('YYYY-MM-DDTHH:mm:ssZ'),
                        ClientId: this.clientId,
                        CompanyId: this.activity.company.id,
                        Description: this.activity.description,
                        UserId: this.userId,
                    };

                    this.$api
                        .post(restUrl, noteData)
                        .then(() => {
                            EventBus.$emit('activity:update');
                            this.$helpers.showSuccessNotification(
                                `A <b>Note</B> ${
                                    this.activity.contact ? 'for ' + this.activity.company.name : ''
                                } has been logged.`,
                            );
                            this.closeWidget();
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification(
                                'An error occurred while adding your activity.',
                                { error },
                            );
                        });
                } else {
                    this.hasDescriptionError = true;
                }
            } else {
                this.$helpers.showFailNotification('No company is selected.', {
                    level: LogLevel.INFO,
                });
            }
        },

        patchNote() {
            const restUrl = `/api/CompanyNotes/${this.activity.id}`;

            if (this.activity.description !== '') {
                this.step = 'saving';

                var changes = [
                    {
                        name: 'Description',
                        value: this.activity.description,
                    },
                    {
                        name: 'ClientId',
                        value: this.clientId,
                    },
                ];

                this.$api
                    .patch(restUrl, changes)
                    .then(() => {
                        EventBus.$emit('activity:update', this.activity);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while saving your activity.',
                            { error },
                        );
                        this.closeWidget();
                    });
            }
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.company-notes-widget .loading-screen {
    margin: 0 auto;
}

.company-notes-widget .form-group--error .error {
    font-size: 12px;
    color: #c00;
    margin-top: 2px;
}

.company-notes-widget .activity-step {
    width: 100%;
}
.company-notes-widget .activity-step .activity-form-row {
    display: flex;
    flex-direction: column;
}

.company-notes-widget .activity-step .activity-form-row.activity-profile-image .profile-image {
    width: 84px;
    height: 84px;
    border-radius: 50%;
    margin-right: 35px;
}

.company-notes-widget .activity-step .activity-form-row .input-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 12px;
}

.company-notes-widget .wrapper.start-date,
.company-notes-widget .wrapper.end-date {
    margin-bottom: 12px;
}

.company-notes-widget .wrapper.end-date {
    display: flex;
    flex-direction: column;
}

.company-notes-widget .wrapper .date-time {
    width: 100%;
    display: flex;
}

.company-notes-widget .activity-step .activity-form-row .input-wrapper .error {
    color: #f30070;
}

.company-notes-widget .activity-step.scrub-step .activity-form-row {
    flex-direction: column;
}

.company-notes-widget .activity-step.saving-step svg {
    width: 100%;
}

.company-notes-widget .action-container {
    display: flex;
}

.company-notes-widget .action-container .btn {
    margin-left: 15px;
}

.company-notes-widget .cancel-container .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.company-notes-widget .cancel-container .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.company-notes-widget .action-container .btn.white-btn {
    background: #ffffff;
    color: #877d88;
    border: 2px solid #e9e9ef;
}

.company-notes-widget .input-wrapper.duration .wrapper {
    display: flex;
}

.company-notes-widget .input-wrapper.duration .wrapper .time-picker {
    align-items: center;
}

.company-notes-widget .input-wrapper.duration .app-input .input-container {
    margin-bottom: 0;
}

.company-notes-widget .input-wrapper.duration .app-input.duration-input {
    margin-right: 15px;
}

.company-notes-widget .wrapper .app-date-picker {
    width: 400px;
}

.company-notes-widget .wrapper .time-picker {
    width: 400px;
}

.company-notes-widget .time-picker .colon {
    padding-top: 6px;
}

.company-notes-widget .time-picker .meridian {
    width: 100px;
}

.company-notes-widget .app-date-picker .vdp-datepicker__calendar {
    left: 0;
    bottom: 40px;
    z-index: 10000;
}

.company-notes-widget .other-participants {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -7px;
}

.company-notes-widget .other-participants .participant {
    color: #ffffff;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 15px;
    margin: 0 7px 9px 7px;
    display: flex;
    align-items: center;
}

.company-notes-widget .other-participants .participant svg {
    margin-left: 12px;
    width: 7px;
    cursor: pointer;
}

.company-notes-widget .other-participants .participant .s3-close-svg .a {
    stroke: #ffffff;
}

.company-notes-widget .activity-participant-search {
    margin-bottom: 10px;
}

.company-notes-widget .app-profile-image .background-image {
    height: 15px;
    width: 15px;
    margin-right: 8px;
}

.company-notes-widget .time-picker .hour-input,
.company-notes-widget .time-picker .minute-input {
    width: 75px;
}

.company-notes-widget .activity-step .activity-form-row .input-wrapper.description {
    margin-bottom: 24px;
}

.company-notes-widget .activity-step .activity-form-row .label {
    display: block;
    font-size: 16px;
    font-family: 'Roboto';
    font-weight: 400;
    margin-bottom: 7px;
}

.company-notes-widget .fr-toolbar .fr-command.fr-btn img {
    margin: 11px;
    width: 17px;
}
</style>
